import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Banner, ButtonAlpha, StackBlock, TextAlpha, LinkButton } from '@yonderland/andes-react';
import { customerMutations } from '@as-react/common-queries';
import { useAemConfig } from '@as-react/common-components';
import { AuthStatusCode, ErrorMessage } from '@as-react/common-types';
import { useMutation } from '@tanstack/react-query';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm, FormProvider } from 'react-hook-form';
import { I18N_NAMESPACES } from '../../../../constants/i18nConstants';
import { GuestLoginBlock } from './GuestLoginBlock';
import { useCheckoutConfig, useCheckoutState } from '../../../../context/CheckoutContext';
import { useGuestLogin } from '../../../../hooks/useGuestLogin';
export const SetPasswordForm = ({ onEditEmailClick }) => {
    const { t } = useTranslation([
        I18N_NAMESPACES.account,
        I18N_NAMESPACES.checkout,
        I18N_NAMESPACES.general,
        I18N_NAMESPACES.navigation,
    ]);
    const { defaultRequestParams, servicesEndpoint, mainWebShop } = useAemConfig();
    const { disableGuestLoginForRegisteredUser } = useCheckoutConfig();
    const [state] = useCheckoutState();
    const { email, accountStatusCode } = state.login;
    const [resetPasswordEmailSent, setResetPasswordEmailSent] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const formProps = useForm({ defaultValues: { email }, mode: 'onTouched' });
    const { sendPasswordResetEmailMutation } = customerMutations({ servicesEndpoint, defaultRequestParams });
    const { mutate: sendPasswordResetEmail, isPending: isSendPasswordPending } = useMutation(sendPasswordResetEmailMutation());
    const { guestLogin, errorMessage: guestErrorMessage, isPending: isGuestLoginPending } = useGuestLogin();
    const otherFasciaEmail = [
        AuthStatusCode.VALID_ACCOUNT_OTHER_FASCIA,
        AuthStatusCode.INVALID_ACCOUNT_OTHER_FASCIA,
    ].includes(accountStatusCode);
    const handleGuestLoginClick = () => guestLogin(email);
    const onSubmit = ({ email }) => {
        sendPasswordResetEmail({ email }, {
            onSuccess: () => {
                setResetPasswordEmailSent(true);
                setErrorMessage('');
            },
            onError: () => {
                setErrorMessage(t(ErrorMessage.GENERAL_ACCOUNT));
            },
        });
    };
    useEffect(() => {
        guestErrorMessage && setErrorMessage(t(guestErrorMessage));
    }, [guestErrorMessage]);
    return (_jsx(FormProvider, Object.assign({}, formProps, { children: _jsx("form", { onSubmit: formProps.handleSubmit(onSubmit), name: "set-password-form", children: _jsxs(StackBlock, { gap: "6", children: [_jsx(TextAlpha, { variant: "headingS", color: "emphasised", children: t([
                            `${I18N_NAMESPACES.checkout}:checkout.set_password.title.${mainWebShop}`,
                            `${I18N_NAMESPACES.checkout}:checkout.set_password.title`,
                        ]) }), !resetPasswordEmailSent && (_jsx(Banner, { text: t(otherFasciaEmail
                            ? [
                                `${I18N_NAMESPACES.checkout}:checkout.set_password.known_email.other_fascia.intro.${mainWebShop}`,
                                `${I18N_NAMESPACES.checkout}:checkout.set_password.known_email.other_fascia.intro`,
                            ]
                            : [
                                `${I18N_NAMESPACES.checkout}:checkout.set_password.known_email.intro.${mainWebShop}`,
                                `${I18N_NAMESPACES.checkout}:checkout.set_password.known_email.intro`,
                            ]), status: "informative" })), errorMessage && _jsx(Banner, { text: errorMessage, status: "error" }), resetPasswordEmailSent && !errorMessage && (_jsx(Banner, { status: "success", text: t([
                            `${I18N_NAMESPACES.checkout}:checkout.set_password.success.${mainWebShop}`,
                            `${I18N_NAMESPACES.checkout}:checkout.set_password.success`,
                        ]) })), !resetPasswordEmailSent && (_jsxs(_Fragment, { children: [_jsxs(TextAlpha, { strong: true, wordBreak: "break", children: [email, ' - ', _jsx(LinkButton, { text: t(`${I18N_NAMESPACES.checkout}:checkout.set_password.edit`), onClick: onEditEmailClick })] }), _jsx(ButtonAlpha, { type: "submit", theme: "branded", state: isSendPasswordPending ? 'loading' : 'interactive', text: t([
                                    `${I18N_NAMESPACES.checkout}:checkout.set_password.action.${mainWebShop}`,
                                    `${I18N_NAMESPACES.checkout}:checkout.set_password.action`,
                                ]), fullWidth: true })] })), !disableGuestLoginForRegisteredUser && (_jsx(GuestLoginBlock, { isLoading: isGuestLoginPending, onGuestLoginClick: handleGuestLoginClick }))] }) }) })));
};
